import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import _ from 'lodash';
import moment from 'moment';
import { CarrierService } from 'common/data';
import { carrierTypes } from 'common/constants';
import { BillingForm } from 'modules/billing/forms';
import BillingViewStore from './BillingViewStore';
import pageOf from './pageOf';

// Matches the import range the weekly job uses, so a backfill stays a manageable report.
const maxRangeDays = 31;
const apiDateFormat = 'YYYY-MM-DD';
const maxSellerCarriers = 1000;
const notBillableValue = 'notBillable';

// The Import Charges page: import charges and match them to sellers.
class BillingChargesViewStore extends BillingViewStore {
	@observable importedCount = null;
	@observable lastImportRange = null;
	@observable isMatchedExpanded = false;
	// Open by default: it's where the work is.
	@observable isUnmatchedExpanded = true;
	@observable isNotBillableExpanded = false;
	// Every seller's FedEx/UPS carrier account, for the manual assignment dropdowns.
	@observable.ref sellerCarriers = null;
	// Manual picks for unmatched packages, by packageKey: { sellerId, carrierId }.
	assignments = observable.map();
	// The seller dropdown's "No seller - don't bill" option.
	notBillableValue = notBillableValue;
	// The page (0-based) each table shows.
	pages = observable({ matched: 0, unmatched: 0, notBillable: 0 });

	form = new BillingForm();

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
		this.carrierService = new CarrierService(rootStore.app.baasic.apiClient);
		this.format = moment.localeData().longDateFormat('L');
		this.today = moment()
			.endOf('day')
			.toDate();
		this.prefillLastWeek();
		this.refresh();
	}

	@action.bound
	prefillLastWeek() {
		const lastWeek = moment().subtract(1, 'week');
		this.form.$('fromDate').set(
			lastWeek
				.clone()
				.startOf('isoWeek')
				.format(this.format)
		);
		this.form.$('toDate').set(
			lastWeek
				.clone()
				.endOf('isoWeek')
				.format(this.format)
		);
	}

	getSelectedRange() {
		const fromDate = moment(this.form.$('fromDate').value, this.format, true);
		const toDate = moment(this.form.$('toDate').value, this.format, true);
		const notificationStore = this.rootStore.notificationStore;
		if (!fromDate.isValid() || !toDate.isValid()) {
			notificationStore.error('INVALIDDATE');
			return null;
		}
		if (fromDate.isAfter(toDate, 'day')) {
			notificationStore.error('BILLING.INVALIDRANGE');
			return null;
		}
		if (toDate.diff(fromDate, 'days') >= maxRangeDays) {
			notificationStore.error(['BILLING.RANGETOOLONG', { maxDays: maxRangeDays }]);
			return null;
		}
		if (toDate.isAfter(moment(), 'day')) {
			notificationStore.error('BILLING.FUTUREDATE');
			return null;
		}
		return {
			fromDate: fromDate.format(apiDateFormat),
			toDate: toDate.format(apiDateFormat),
			fromDateLabel: fromDate.format(this.format),
			toDateLabel: toDate.format(this.format),
		};
	}

	@action.bound
	toggleMatchedExpanded() {
		this.isMatchedExpanded = !this.isMatchedExpanded;
	}

	@action.bound
	toggleUnmatchedExpanded() {
		this.isUnmatchedExpanded = !this.isUnmatchedExpanded;
	}

	@action.bound
	toggleNotBillableExpanded() {
		this.isNotBillableExpanded = !this.isNotBillableExpanded;
	}

	@computed get readyAssignments() {
		return _.filter(
			_.map(this.unmatchedPackages, row => ({ row, pick: this.assignments.get(packageKey(row)) })),
			x => x.pick && (x.pick.sellerId === notBillableValue || (x.pick.sellerId && x.pick.carrierId))
		).map(({ row, pick }) =>
			pick.sellerId === notBillableValue
				? { trackingNumber: row.trackingNumber, carrier: row.carrier, isNotBillable: true }
				: {
						trackingNumber: row.trackingNumber,
						carrier: row.carrier,
						sellerId: pick.sellerId,
						carrierId: pick.carrierId,
				  }
		);
	}

	// Tracking number matches come from the order itself, so they can't be sent back to unmatched here.
	canUnmatch(row) {
		return !!row.trackingNumber && row.matchedBy !== 'TrackingNumber';
	}

	// Built once per load of the carriers, not per row: every unmatched row's dropdowns read from these.
	@computed get sellerOptionsByCarrierType() {
		return _.mapValues(_.groupBy(this.sellerCarriers, 'carrierType'), carriers =>
			_.sortBy(_.uniqBy(carriers.map(x => ({ value: x.sellerId, label: x.sellerName })), 'value'), x =>
				_.toLower(x.label)
			)
		);
	}

	@computed get carrierOptionsBySellerAndType() {
		return _.mapValues(_.groupBy(this.sellerCarriers, x => `${x.sellerId}|${x.carrierType}`), carriers =>
			carriers.map(x => ({ value: x.carrierId, label: x.label }))
		);
	}

	sellerOptions(carrierType) {
		return this.sellerOptionsByCarrierType[carrierType] || [];
	}

	carrierOptions(sellerId, carrierType) {
		return this.carrierOptionsBySellerAndType[`${sellerId}|${carrierType}`] || [];
	}

	// The tables show one page of rows at a time: thousands of rows, each with two dropdowns, froze the browser.
	@computed get matchedPage() {
		return pageOf(this.matchedPackages, this.pages.matched);
	}

	@computed get unmatchedPage() {
		return pageOf(this.unmatchedPackages, this.pages.unmatched);
	}

	@computed get notBillablePage() {
		return pageOf(this.notBillablePackages, this.pages.notBillable);
	}

	@action.bound
	setPage(table, page) {
		this.pages[table] = page;
	}

	getAssignment(row) {
		return this.assignments.get(packageKey(row)) || { sellerId: '', carrierId: '' };
	}

	@action.bound
	setAssignedSeller(row, sellerId) {
		const carriers = sellerId === notBillableValue ? [] : this.carrierOptions(sellerId, row.carrier);
		this.assignments.set(packageKey(row), {
			sellerId,
			carrierId: carriers.length === 1 ? carriers[0].value : '',
		});
	}

	@action.bound
	setAssignedCarrier(row, carrierId) {
		this.assignments.set(packageKey(row), { ...this.getAssignment(row), carrierId });
	}

	// Pulls a date range from Lojistic by hand, for a backfill or a week the scheduled job missed.
	@action.bound
	async importCharges() {
		const range = this.getSelectedRange();
		if (!range) return;
		this.importedCount = null;
		this.lastImportRange = range;
		await this.updateMatches('BILLING.STEPIMPORT', async () => {
			const response = await this.billingService.importCharges(range.fromDate, range.toDate);
			runInAction(() => (this.importedCount = response.data.inserted));
			this.setStep('BILLING.STEPMATCH');
			await this.billingService.calculateUpcharges();
		});
	}

	@action.bound
	async saveAssignments() {
		const assignments = this.readyAssignments;
		if (assignments.length === 0) return;
		await this.updateMatches('BILLING.STEPASSIGN', async () => {
			const response = await this.billingService.assign(assignments);
			this.rootStore.notificationStore.success(['BILLING.ASSIGNED', { count: response.data.assigned }]);
		});
	}

	@action.bound
	async unmatch(row) {
		await this.updateMatches('BILLING.STEPUNMATCH', () =>
			this.billingService.unassign(row.trackingNumber, row.carrier)
		);
	}

	async beforeLoadMatches() {
		if (this.sellerCarriers == null) {
			await this.loadSellerCarriers();
		}
	}

	// Keeps picks still being made on packages that are still unmatched, and suggests a seller for the rest.
	onMatchesLoaded() {
		const unmatchedKeys = new Set(_.map(this.unmatchedPackages, packageKey));
		_.each(Array.from(this.assignments.keys()), key => {
			if (!unmatchedKeys.has(key)) this.assignments.delete(key);
		});
		_.each(this.unmatchedPackages, row => {
			if (row.trackingNumber && row.sellerId && !this.assignments.has(packageKey(row))) {
				this.setAssignedSeller(row, row.sellerId);
			}
		});
	}

	async loadSellerCarriers() {
		const response = await this.carrierService.find({
			carrierTypeNames: [carrierTypes.fedex, carrierTypes.ups],
			embed: ['seller', 'carrierType'],
			pageSize: maxSellerCarriers,
		});
		runInAction(() => {
			this.sellerCarriers = _.filter(response.data.item, x => x.sellerId && x.seller).map(x => ({
				carrierId: x.id,
				carrierType: x.carrierType.name,
				sellerId: x.sellerId,
				sellerName: x.seller.applicationTitle,
				label: x.surplusCharge == null ? x.name : `${x.name} (${surchargeLabel(x)})`,
			}));
		});
	}
}

function packageKey(row) {
	return `${row.trackingNumber}|${row.carrier}`;
}

function surchargeLabel(carrier) {
	return carrier.isPercentage ? `${carrier.surplusCharge}%` : `$${carrier.surplusCharge}`;
}

export default BillingChargesViewStore;
